import React, { useEffect, useState } from 'react';
import {
	Autocomplete,
	Box,
	Button,
	CircularProgress,
	Collapse,
	Divider,
	IconButton,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddCommentIcon from '@mui/icons-material/AddComment';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import getCase from 'api/comments/getCase';
import getStatuses, { CANtrackStatus } from 'api/comments/getStatuses';
import { convertToCANtrackCase } from 'containers/pages/comments/utils/convertToCANtrackCase';
import { CANtrackCase } from 'containers/pages/comments/types/comment';
import useNotificationProvider from 'hooks/notificationBar/useNotificationProvider';

const CANTRACK_CASE_URL = '/cantrackv3/cases';

export interface CANtrackChangeData {
	statusId: number | null;
	statusComment: string;
	hasChanges: boolean;
}

interface CANtrackSectionProps {
	// Case linking (state managed by parent)
	cases: CANtrackCase[];
	loadingCases: boolean;
	selectedCase: CANtrackCase | null;
	onCaseChange: (newCase: CANtrackCase | null) => void;
	onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	// CANtrack actions
	onLogComment: () => void;
	// Status change reporting
	onCANtrackChange: (data: CANtrackChangeData) => void;
}

const CANtrackSection: React.FC<CANtrackSectionProps> = ({
	cases,
	loadingCases,
	selectedCase,
	onCaseChange,
	onSearchChange,
	onLogComment,
	onCANtrackChange,
}) => {
	const { notifyError } = useNotificationProvider();
	const [expanded, setExpanded] = useState(false);
	const [caseDetails, setCaseDetails] = useState<CANtrackCase | null>(null);
	const [statuses, setStatuses] = useState<CANtrackStatus[]>([]);
	const [selectedStatus, setSelectedStatus] = useState<CANtrackStatus | null>(null);
	const [statusComment, setStatusComment] = useState('');
	const [loading, setLoading] = useState(false);

	// Track original values to detect changes
	const [originalStatusId, setOriginalStatusId] = useState<string | null>(null);
	const [originalStatusComment, setOriginalStatusComment] = useState('');

	// Fetch case details + statuses when a case is selected
	useEffect(() => {
		if (!selectedCase) {
			setCaseDetails(null);
			setStatuses([]);
			setSelectedStatus(null);
			setStatusComment('');
			setOriginalStatusId(null);
			setOriginalStatusComment('');
			onCANtrackChange({ statusId: null, statusComment: '', hasChanges: false });
			return;
		}

		let cancelled = false;

		async function fetchData() {
			setLoading(true);
			try {
				const [caseRaw, statusList] = await Promise.all([
					getCase(selectedCase!.id),
					getStatuses(),
				]);

				if (cancelled) return;

				const converted = convertToCANtrackCase(caseRaw);
				setCaseDetails(converted);
				setStatuses(statusList);

				// Set current status
				let currentStatus: CANtrackStatus | null = null;
				if (converted.statusId) {
					currentStatus = statusList.find(
						(s) => parseInt(s.Id) === converted.statusId,
					) || null;
				}
				setSelectedStatus(currentStatus);
				setOriginalStatusId(currentStatus?.Id || null);

				// Set current status comment
				const comment = converted.statusComment || '';
				setStatusComment(comment);
				setOriginalStatusComment(comment);

				onCANtrackChange({ statusId: null, statusComment: '', hasChanges: false });
			} catch (error) {
				console.error('[CANtrackSection] Failed to fetch CANtrack data:', error);
				notifyError('Failed to load CANtrack case details');
			} finally {
				if (!cancelled) setLoading(false);
			}
		}

		fetchData();
		return () => {
			cancelled = true;
		};
	}, [selectedCase?.id]);

	// Report changes to parent
	useEffect(() => {
		const statusChanged = selectedStatus?.Id !== originalStatusId;
		const commentChanged = statusComment !== originalStatusComment;
		const hasChanges = statusChanged || commentChanged;

		onCANtrackChange({
			statusId: selectedStatus ? parseInt(selectedStatus.Id) : null,
			statusComment,
			hasChanges,
		});
	}, [selectedStatus, statusComment, originalStatusId, originalStatusComment]);

	return (
		<>
			<Divider sx={{ my: 1 }} />
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
				{/* Header with expand toggle */}
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						cursor: selectedCase ? 'pointer' : 'default',
					}}
					onClick={() => selectedCase && setExpanded(!expanded)}
				>
					<Typography variant="subtitle2" color="text.secondary">
						CANtrack
					</Typography>
					{selectedCase && (
						<IconButton size="small">
							{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
						</IconButton>
					)}
				</Box>

				{/* Case linking — always visible */}
				<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
					<Autocomplete
						options={cases}
						loading={loadingCases}
						value={selectedCase}
						onChange={(_, newValue) => onCaseChange(newValue)}
						getOptionLabel={(option) => option.title || 'Unnamed Case'}
						isOptionEqualToValue={(option, value) => option.id === value.id}
						sx={{ flex: 1 }}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Link to Case (optional)"
								onChange={onSearchChange}
								InputProps={{
									...params.InputProps,
									endAdornment: (
										<>
											{loadingCases && <CircularProgress size={20} />}
											{params.InputProps.endAdornment}
										</>
									),
								}}
							/>
						)}
					/>
					<Tooltip title={selectedCase ? 'Open case' : 'Select a case first'}>
						<span>
							<IconButton
								disabled={!selectedCase}
								onClick={(e) => {
									e.stopPropagation();
									if (selectedCase) {
										window.open(
											`${CANTRACK_CASE_URL}/${selectedCase.id}`,
											'_blank',
										);
									}
								}}
								sx={{ mt: 1 }}
							>
								<OpenInNewIcon />
							</IconButton>
						</span>
					</Tooltip>
				</Box>

				{/* Expandable section — only when a case is linked */}
				{selectedCase && (
					<Collapse in={expanded}>
						{loading ? (
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
								<CircularProgress size={18} />
								<Typography variant="body2" color="text.secondary">
									Loading CANtrack details...
								</Typography>
							</Box>
						) : caseDetails ? (
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
								{/* CGPD Breadcrumb */}
								<Typography variant="caption" color="textSecondary">
									CGPD:{' '}
									<Typography component="span" variant="caption" color="primary">
										{caseDetails.customerName || '\u2014'}
									</Typography>
									{' / '}
									<Typography component="span" variant="caption" color="success.light">
										{caseDetails.gpd.group.title || '\u2014'}
									</Typography>
									{' / '}
									<Typography component="span" variant="caption" color="warning.light">
										{caseDetails.gpd.project.title || '\u2014'}
									</Typography>
									{' / '}
									<Typography component="span" variant="caption" color="error.light">
										{caseDetails.gpd.deliverable.title || '\u2014'}
									</Typography>
								</Typography>

								{/* Status dropdown */}
								<Autocomplete
									options={statuses}
									getOptionLabel={(option) => option.Title}
									value={selectedStatus}
									onChange={(_, newValue) => setSelectedStatus(newValue)}
									isOptionEqualToValue={(option, value) => option.Id === value.Id}
									size="small"
									renderInput={(params) => (
										<TextField {...params} label="Case Status" variant="outlined" />
									)}
								/>

								{/* Status Comment */}
								<TextField
									label="Status Comment"
									value={statusComment}
									onChange={(e) => setStatusComment(e.target.value)}
									multiline
									minRows={2}
									maxRows={4}
									size="small"
									fullWidth
								/>

								{/* Log Comment button */}
								<Button
									variant="outlined"
									startIcon={<AddCommentIcon />}
									onClick={(e) => {
										e.stopPropagation();
										onLogComment();
									}}
									sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
								>
									Log CANtrack Comment
								</Button>
							</Box>
						) : (
							<Typography variant="body2" color="error">
								Failed to load case details.
							</Typography>
						)}
					</Collapse>
				)}
			</Box>
		</>
	);
};

export default CANtrackSection;
